import type { ReactNode } from "react";
import { Navigate, useLocation, useRouteLoaderData } from "react-router-dom";
import { isAuthorized } from "@/lib/auth";
import type { AuthResponse } from "@/types/auth";

type ProtectedRouteProps = {
  children: ReactNode;
  roles?: readonly string[];
};

export function ProtectedRoute({ children, roles }: ProtectedRouteProps) {
  const location = useLocation();
  const rootData = useRouteLoaderData("root") as AuthResponse | undefined;
  const user = rootData?.user ?? null;

  if (!user) {
    const redirectTo = encodeURIComponent(
      `${location.pathname}${location.search}${location.hash}`,
    );
    return <Navigate to={`/login?redirectTo=${redirectTo}`} replace />;
  }

  if (!isAuthorized(user, roles)) {
    // Diagnostic — when QA hits /unauthorized, the console explains exactly
    // which role was rejected and which were allowed. Otherwise the only
    // signal is the generic page text and you have to dig through the
    // route table to figure out which guard fired.
    if (typeof console !== "undefined") {
      console.warn(
        `[ProtectedRoute] denied ${location.pathname} — user role "${user.role}" not in [${(roles ?? []).join(", ")}]`,
      );
    }
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}